import { Form, notification } from 'antd';
import includes from 'lodash/includes';
import { Component } from 'react';

import { BorderedTitle } from '@/components/BorderedTitle';
import { withFeatureFlag } from '@/components/FeatureFlag/FeatureFlag';
import Input from '@/components/FormInput';
import { LoginCard, LoginSubmit } from '@/components/Login';
import PasswordRequirements from '@/components/PasswordRequirements';
import {
  createPasswordOnChangeHandler,
  getPasswordConfirmationRules,
  getPasswordRules,
} from '@/components/PasswordRequirements/passwordValidation';
import Redirect from '@/components/Redirect';
import Session from '@/services/Session';
import { completeRegistration as completeRegistrationAPI } from '@/services/api';
import { validatePhone } from '@/utils';

import Confirmed from './Confirmed';
import './Login.css';
import Migrated from './Migrated';
import OldConfirm from './deprecated/OldConfirm';
import { isPractitioner } from './utils';

class Confirm extends Component {
  state = {
    isConfirmed: false,
    loading: false,
  };

  handleSuccess = () => {
    this.setState({
      isConfirmed: true,
      loading: false,
    });
  };

  completeRegistration = async (values) => {
    if (this.state.loading) {
      return;
    }

    this.setState({
      loading: true,
    });

    try {
      const token = Session.signature();
      await completeRegistrationAPI(token, values);
      this.handleSuccess();
    } catch (error) {
      const { response } = error;
      const { form } = this.props;

      // Display field-specific errors
      const hasFieldErrors = response?.errors?.some((res) => res.id);
      if (response?.errors) {
        response.errors.forEach((res) => {
          if (res.id) {
            form.setFields({
              [res.id]: {
                value: values[res.id] || form.getFieldValue(res.id),
                errors: [new Error(res.detail)],
              },
            });
          }
        });
      }

      // Don't show notification for field-specific validation errors
      const generalError =
        response?.errors?.find((res) => !res.id)?.detail ||
        (!hasFieldErrors && (response?.error || error.message));

      if (generalError) {
        notification.error({ message: 'Error', description: String(generalError) });
      }

      this.setState({ loading: false });
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { form } = this.props;

    if (this.state.loading) {
      return;
    }

    form.validateFieldsAndScroll(async (err, values) => {
      if (err) {
        this.setState({
          loading: false,
        });
        return;
      }
      this.completeRegistration(values);
    });
  };

  renderInformationItems = () => {
    const {
      form: { getFieldDecorator },
    } = this.props;
    const { user = {} } = Session.get();

    const formItems = [
      {
        label: 'First Name',
        name: 'first_name',
        initialValue: user.first_name,
        rules: [
          {
            required: true,
            message: 'Please enter a first name',
          },
        ],
        placeholder: 'e.g. Jen',
      },
      {
        label: 'Last Name',
        name: 'last_name',
        initialValue: user.last_name,
        rules: [
          {
            required: true,
            message: 'Please enter a last_name',
          },
        ],
        placeholder: 'e.g. Johnson',
      },
      {
        label: 'Email',
        name: 'email',
        disabled: true,
        initialValue: user.email,
      },
      {
        label: 'Phone Number',
        name: 'phone_number',
        type: 'phone_number',
        initialValue: user.phone_number,
        rules: [
          {
            validator: validatePhone,
          },
        ],
      },
    ];

    return formItems.map((itemProps) => (
      <Input {...itemProps} key={itemProps.name} getFieldDecorator={getFieldDecorator} />
    ));
  };

  renderAuthItems = () => {
    const {
      form: { getFieldDecorator, getFieldValue },
      form,
    } = this.props;
    const { user = {} } = Session.get();
    const { registration_type, roles } = user.user_metadata;
    const invitedPatient = registration_type === 'invite' && includes(roles, 'patient');

    const passwordRules = getPasswordRules();
    const passwordConfirmationRules = getPasswordConfirmationRules(getFieldValue);
    const passwordOnChange = createPasswordOnChangeHandler(form, 'password_confirmation');

    const formItems = [
      {
        label: 'Username',
        name: 'username',
        disabled: isPractitioner(user),
        initialValue: invitedPatient ? null : user.username,
        rules: [
          {
            required: true,
            message: 'Please enter a username',
          },
        ],
      },
      {
        label: 'Password',
        name: 'password',
        type: 'password',
        rules: passwordRules,
        validateFirst: true,
        decoratorOptions: {
          onChange: passwordOnChange,
        },
      },
      {
        label: 'Password confirmation',
        name: 'password_confirmation',
        type: 'password',
        rules: passwordConfirmationRules,
        validateFirst: true,
      },
    ];

    return formItems.map((itemProps) => (
      <Input {...itemProps} key={itemProps.name} getFieldDecorator={getFieldDecorator} />
    ));
  };

  render() {
    const { isConfirmed } = this.state;
    const session = Session.get();

    if (!Session.valid()) {
      return <Redirect to="/error" />;
    }

    if (session.user && session.user.confirmed_at) {
      return <Redirect to="" baseUrl={session.client.client_uri} />;
    }

    const { client, user = {} } = session;
    const { registration_type } = user.user_metadata;
    const isMigratedUser = registration_type === 'migrated';

    if (isConfirmed) {
      return <Confirmed {...this.props} client={client} user={user} />;
    }

    if (isMigratedUser) {
      return <Migrated {...this.props} onSubmit={this.completeRegistration} />;
    }

    return (
      <LoginCard logo={client.logo_uri}>
        <Form onSubmit={this.handleSubmit}>
          <BorderedTitle>Confirm Your Information</BorderedTitle>
          {this.renderInformationItems()}
          <BorderedTitle>Set Your Username & Password</BorderedTitle>
          {this.renderAuthItems()}
          <PasswordRequirements />
          <LoginSubmit loading={this.state.loading}>Confirm</LoginSubmit>
        </Form>
        <div>
          By clicking Confirm, you accept the&nbsp;
          <a
            href="https://keet-email-assets.s3.us-west-2.amazonaws.com/2022/KEET+-+EULA+revised+2_2022+FINAL.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            End User Terms
          </a>
          &nbsp;and&nbsp;
          <a
            href="https://s3-us-west-2.amazonaws.com/end-user-agreement/Keet+-+Privacy+Policy+.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </LoginCard>
    );
  }
}

export default Form.create()(
  withFeatureFlag('ketKeet3316RiskPasswordRemediation', Confirm, OldConfirm),
);
